import { StatsigClient } from '@statsig/react-bindings';
import User from '@/types/user';
import Nullable from '@/types/nullable';
import sentryUtils from '@/utils/3rd-party/sentry';

type StatsigConfig = {
  clientKey: string;
  environment?: string;
};

const getUserContext = (user?: Nullable<User>) => ({
  userID: user?.id,
  customIDs: { enterpriseId: user?.enterprise?._id, enterpriseKey: user?.enterprise?.key },
});

// Note : using the factory pattern to allow for easy testing (avoiding the import meta env variables in tests)
const createStatsigClientManager = (config: StatsigConfig) => {
  let statsigClient: Nullable<StatsigClient> = null;

  const configureStatsigClient = async (user?: Nullable<User>): Promise<Nullable<StatsigClient>> => {
    if (statsigClient) {
      await updateStatsigUser(user);
      return statsigClient;
    }

    if (!config.clientKey) {
      return null;
    }

    try {
      // Note that Statsig will default to production if the environment is not set
      statsigClient = new StatsigClient(config.clientKey, getUserContext(user), { environment: { tier: config.environment } });

      await statsigClient.initializeAsync();

      return statsigClient;
    } catch (error) {
      sentryUtils.captureError(error as Error);
      return null;
    }
  };

  const getStatsigClient = (): Nullable<StatsigClient> => statsigClient;

  const updateStatsigUser = async (user?: Nullable<User>): Promise<void> => {
    try {
      if (statsigClient) {
        await statsigClient.updateUserAsync(getUserContext(user));
      }
    } catch (error) {
      sentryUtils.captureError(error as Error);
      statsigClient = null;
    }
  };

  return {
    configureStatsigClient,
    getStatsigClient,
  };
};

// Create default instance with environment variables
const defaultConfig: StatsigConfig = {
  clientKey: import.meta.env.REACT_APP_STATSIG_CLIENT_KEY || '',
  environment: import.meta.env.REACT_APP_STATSIG_ENVIRONMENT,
};

const defaultManager = createStatsigClientManager(defaultConfig);

// Export default instance methods
export const { configureStatsigClient } = defaultManager;
export const { getStatsigClient } = defaultManager;

// Export factory for testing
export { createStatsigClientManager };
